import { supabase } from '@/core/database/supabase';
import { permissionEngine } from '@/core/permissions/permissionEngine';
import type { EstimateNorm, EstimateNormInput, EstimateNormResource, EstimateNormResourceInput } from '../types/estimateNorms.types';
import type { EstimateItem } from '../types/estimate.types';

function buildCode(prefix: string) {
  return `${prefix}-${Math.floor(10000 + Math.random() * 90000)}`;
}

export const estimateNormApplyService = {
  async getNorms(companyId: string, categoryCode?: string): Promise<EstimateNorm[]> {
    let query = supabase.from('estimate_norms' as any).select('*').eq('company_id', companyId);
    
    if (categoryCode && categoryCode !== 'all') {
      query = query.eq('category_code', categoryCode);
    }
    
    const { data, error } = await query.order('norm_code', { ascending: true });
    
    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('estimate_norms');
      if (missingTable) return [];
      throw error;
    }
    
    return data as EstimateNorm[];
  },

  async getNormById(id: string): Promise<{ norm: EstimateNorm; resources: EstimateNormResource[] } | null> {
    const { data, error } = await supabase
      .from('estimate_norms' as any)
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      const missingTable = error.code === '42P01' || error.message?.includes('estimate_norms');
      if (missingTable) return null;
      throw error;
    }
    if (!data) return null;

    const { data: resources, error: resourceError } = await supabase
      .from('estimate_norm_resources' as any)
      .select('*')
      .eq('norm_id', id)
      .order('created_at', { ascending: true });

    if (resourceError) throw resourceError;
    return { norm: data as EstimateNorm, resources: (resources || []) as EstimateNormResource[] };
  },

  async createNorm(userId: string, input: EstimateNormInput, resources: EstimateNormResourceInput[]): Promise<string> {
    await permissionEngine.assert(userId, 'estimates.norms.create', { companyId: input.company_id });
    const { data, error } = await supabase
      .from('estimate_norms' as any)
      .insert({
        ...input,
        norm_code: input.norm_code?.trim() || buildCode('DM'),
        status_code: input.status_code || 'active',
      })
      .select('id')
      .single();

    if (error) throw error;
    const normId = String((data as any).id);

    if (resources.length > 0) {
      const { error: resourceError } = await supabase.from('estimate_norm_resources' as any).insert(
        resources.map((resource) => ({
          ...resource,
          company_id: input.company_id,
          norm_id: normId,
          norm_quantity: resource.norm_quantity ?? 0,
          waste_percent: resource.waste_percent ?? 0,
        })),
      );
      if (resourceError) throw resourceError;
    }

    return normId;
  },

  async updateNorm(userId: string, id: string, companyId: string, input: Partial<EstimateNormInput>, resources?: EstimateNormResourceInput[]): Promise<void> {
    await permissionEngine.assert(userId, 'estimates.norms.update', { companyId });
    const { error } = await supabase
      .from('estimate_norms' as any)
      .update({ ...input, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
    if (!resources) return;

    const { error: deleteError } = await supabase.from('estimate_norm_resources' as any).delete().eq('norm_id', id);
    if (deleteError) throw deleteError;
    if (resources.length === 0) return;

    const { error: insertError } = await supabase.from('estimate_norm_resources' as any).insert(
      resources.map((resource) => ({
        ...resource,
        company_id: companyId,
        norm_id: id,
        norm_quantity: resource.norm_quantity ?? 0,
        waste_percent: resource.waste_percent ?? 0,
      })),
    );
    if (insertError) throw insertError;
  },

  async applyNormToItem(item: EstimateItem, normId: string): Promise<void> {
    // 1. Lấy chi tiết tài nguyên của định mức
    const { data: normResources, error: normErr } = await supabase
      .from('estimate_norm_resources' as any)
      .select('*')
      .eq('norm_id', normId);
      
    if (normErr) throw normErr;
    if (!normResources || normResources.length === 0) return;

    // 2. Xóa các tài nguyên cũ của hạng mục này (trước khi áp mới)
    await supabase.from('estimate_item_resources' as any).delete().eq('estimate_item_id', item.id);

    // 3. Tính toán và chèn tài nguyên mới dựa trên khối lượng hạng mục
    const resourcesToInsert = normResources.map(nr => {
       const quantity = Number(nr.norm_quantity || 0) * Number(item.quantity || 0);
       return {
         company_id: item.company_id,
         project_id: item.project_id,
         estimate_id: item.estimate_id,
         estimate_item_id: item.id,
         resource_type: nr.resource_type,
         resource_id: nr.resource_id,
         resource_code: nr.resource_code,
         resource_name: nr.resource_name,
         unit: nr.unit,
         norm_quantity: Number(nr.norm_quantity || 0),
         quantity: quantity,
         waste_factor: Number(nr.waste_percent || 0),
         unit_price: 0, // Đơn giá sẽ được tính ở Phase 6
         total_amount: 0,
       };
    });

    const { error: insertErr } = await supabase.from('estimate_item_resources' as any).insert(resourcesToInsert);
    if (insertErr) throw insertErr;
  }
};
